import {
  CUSTOMER_CSRF_HEADER,
  CUSTOMER_SESSION_COOKIE,
} from '../contracts/customer-constants.js';
import { errorFromBody, CustomerApiError, toCustomerApiError } from './errors.js';
import type { ResolvedCustomerClientConfig } from './config.js';

/**
 * The single HTTP seam of the customer client.
 *
 * Everything that talks to a server goes through here, which is what makes the
 * rest of the client testable with an injected `fetch` and what guarantees that
 * four rules hold everywhere rather than in each call site:
 *
 *  1. **Cookies, never tokens in JS.** Requests carry `credentials: 'include'`
 *     so the browser attaches the httpOnly customer session cookie. No code in
 *     this package ever reads or stores an access token.
 *  2. **CSRF on every cookie-authenticated write.** The double-submit token from
 *     the session probe is echoed in `x-tamoomeh-csrf`.
 *  3. **Bounded time.** Every request has a timeout, so a hung network cannot
 *     leave the widget spinning forever.
 *  4. **One error shape.** A non-2xx body, a timeout and a dropped connection
 *     all surface as `CustomerApiError`.
 */

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** Which base URL a request resolves against. */
export type RequestTarget = 'authority' | 'bridge';

export interface TransportRequest {
  /** Path beginning with `/`, or an absolute URL. */
  path: string;
  method?: HttpMethod;
  body?: unknown;
  /**
   * A multipart body, mutually exclusive with `body`.
   *
   * When present the browser must set the `Content-Type` boundary itself, so the
   * JSON header is deliberately not added. The receipt upload is the only user.
   */
  form?: FormData;
  query?: Record<string, string | number | boolean | undefined | null>;
  headers?: Record<string, string>;
  /** Attach the double-submit CSRF header. Defaults to true for unsafe methods. */
  csrf?: boolean;
  idempotencyKey?: string;
  signal?: AbortSignal;
  timeoutMs?: number;
  target?: RequestTarget;
  /** Allow a bounded retry on a transport failure. Defaults to true for GET. */
  retryable?: boolean;
}

export interface TransportResponse<T> {
  data: T;
  status: number;
}

/** Reads the CSRF token the auth layer currently holds. */
export interface CsrfTokenSource {
  readonly csrfToken: string | null;
}

const CORRELATION_HEADER = 'x-request-id';

export class HttpTransport {
  private readonly config: ResolvedCustomerClientConfig;
  private readonly fetchImpl: typeof fetch;
  private csrfSource: CsrfTokenSource | null = null;

  constructor(config: ResolvedCustomerClientConfig, csrfSource?: CsrfTokenSource) {
    this.config = config;
    // Resolved lazily rather than bound here. `globalThis.fetch` does not exist in
    // every environment this module is loaded into — a jsdom test, an older
    // browser, a stripped-down runtime — and `undefined.bind` would throw during
    // *construction*, which the browser entry point catches and reports as a
    // configuration error. That is a misleading diagnosis for "this runtime has no
    // fetch", so the failure is deferred to the first request and named correctly.
    const globalFetch =
      typeof globalThis.fetch === 'function' ? globalThis.fetch.bind(globalThis) : null;
    this.fetchImpl =
      config.fetchImpl ??
      ((input, init) => {
        if (!globalFetch) {
          return Promise.reject(
            new CustomerApiError(
              'NETWORK_ERROR',
              'این مرورگر از درخواست‌های شبکه پشتیبانی نمی‌کند. لطفاً مرورگر خود را به‌روزرسانی کنید.',
            ),
          );
        }
        return globalFetch(input, init);
      });
    this.csrfSource = csrfSource ?? null;
  }

  /** Late binding avoids a construction cycle between transport and auth. */
  setCsrfSource(source: CsrfTokenSource | null): void {
    this.csrfSource = source;
  }

  async send<T>(request: TransportRequest): Promise<TransportResponse<T>> {
    const method = request.method ?? 'GET';
    const target = request.target ?? 'authority';
    const url = this.buildUrl(request.path, request.query, target);
    const unsafe = method !== 'GET';
    const wantsCsrf = request.csrf ?? unsafe;
    const maxAttempts = (request.retryable ?? method === 'GET') ? this.config.retries + 1 : 1;

    let lastError: CustomerApiError | null = null;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        return await this.attempt<T>(url, method, request, wantsCsrf, target);
      } catch (error) {
        const normalized = toCustomerApiError(error);
        lastError = normalized;
        const transient = normalized.code === 'NETWORK_ERROR' || normalized.code === 'TIMEOUT';
        if (!transient || attempt === maxAttempts) throw normalized;
        if (this.config.debug) {
          // eslint-disable-next-line no-console
          console.warn(`[tamoomeh] retrying ${method} ${url} after ${normalized.code}`);
        }
      }
    }
    throw lastError ?? CustomerApiError.unknown();
  }

  private async attempt<T>(
    url: string,
    method: HttpMethod,
    request: TransportRequest,
    wantsCsrf: boolean,
    target: RequestTarget,
  ): Promise<TransportResponse<T>> {
    const controller = new AbortController();
    const timeoutMs = request.timeoutMs ?? this.config.timeoutMs;
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const onAbort = (): void => controller.abort();
    request.signal?.addEventListener('abort', onAbort);

    const headers: Record<string, string> = {
      accept: 'application/json',
      [CORRELATION_HEADER]: newCorrelationId(),
      ...(request.headers ?? {}),
    };
    if (request.body !== undefined && request.form === undefined) {
      headers['content-type'] = 'application/json';
    }
    if (request.idempotencyKey) headers['idempotency-key'] = request.idempotencyKey;

    if (wantsCsrf) {
      const token = this.csrfSource?.csrfToken ?? null;
      if (token) headers[CUSTOMER_CSRF_HEADER] = token;
    }
    // A relying site must prove its own origin to its bridge endpoint; the
    // bridge is same-origin, so the browser sets Origin on unsafe methods only.
    if (target === 'bridge' && this.config.authMode === 'sso') {
      headers['x-tamoomeh-bridge'] = '1';
    }

    let response: Response;
    try {
      response = await this.fetchImpl(url, {
        method,
        headers,
        // The customer session is an httpOnly cookie. `include` is required
        // even for same-origin bridge calls so the relying party's cookie is
        // attached on every request.
        credentials: 'include',
        cache: 'no-store',
        redirect: 'error',
        signal: controller.signal,
        ...(request.form !== undefined
          ? { body: request.form }
          : request.body === undefined
            ? {}
            : { body: JSON.stringify(request.body) }),
      });
    } catch (error) {
      if (controller.signal.aborted && !request.signal?.aborted) {
        throw CustomerApiError.timeout();
      }
      throw toCustomerApiError(error);
    } finally {
      clearTimeout(timer);
      request.signal?.removeEventListener('abort', onAbort);
    }

    const payload = await readBody(response);
    if (!response.ok) {
      throw errorFromBody(payload, response.status).withRequestId(response.headers.get(CORRELATION_HEADER) ?? undefined);
    }
    return { data: payload as T, status: response.status };
  }

  private buildUrl(
    path: string,
    query: TransportRequest['query'],
    target: RequestTarget,
  ): string {
    const base = target === 'bridge' ? bridgeBase(this.config) : this.config.apiBaseUrl;
    if (target === 'authority' && !base) {
      // Only reachable if a relying-site config tried to call Core directly,
      // which it has no credential for. Fail loudly rather than issuing a request
      // against whatever relative path happens to result.
      throw new CustomerApiError(
        'BRIDGE_UNAVAILABLE',
        'این نسخه از سرویس درخواست برای تماس مستقیم با سرور پیکربندی نشده است.',
      );
    }
    const absolute = /^https?:\/\//i.test(path) ? path : `${base}${path.startsWith('/') ? '' : '/'}${path}`;
    if (!query) return absolute;
    const url = new URL(absolute, globalThis.location?.href);
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null) continue;
      url.searchParams.set(key, String(value));
    }
    return url.toString();
  }
}

/**
 * The origin the relying-party endpoints live on.
 *
 * They are always same-origin with the page, so a relative URL is the correct
 * answer and an absolute one is resolved against the current document.
 */
function bridgeBase(config: ResolvedCustomerClientConfig): string {
  const first = config.bridge?.sessionUrl ?? '';
  if (/^https?:\/\//i.test(first)) {
    return new URL(first).origin;
  }
  return '';
}

async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    // A bridge that answers with an HTML error page (a misconfigured rewrite,
    // for instance) must not surface as a JSON parse crash.
    return { error: { code: 'BRIDGE_INVALID_RESPONSE', message: text.slice(0, 200) } };
  }
}

function newCorrelationId(): string {
  const cryptoObj = globalThis.crypto;
  if (cryptoObj && typeof cryptoObj.randomUUID === 'function') return cryptoObj.randomUUID();
  return `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Cookie name the transport expects the server to set (documentation value). */
export const SESSION_COOKIE_NAME = CUSTOMER_SESSION_COOKIE;
